// Desktop Create Organization Side Panel - Refactored to use shared hook
import React, { useEffect } from 'react';
import { View, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Text } from '@/components/common/text';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { GlassFormInput } from '@/components/admin/redesign';
import { useOrganizationForm } from '@/hooks/admin/useOrganizationForm';
import { useOrganizationFormStyles } from './organizationFormStyles';

interface DesktopCreateOrganizationPanelProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DesktopCreateOrganizationPanel: React.FC<DesktopCreateOrganizationPanelProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const styles = useOrganizationFormStyles();
  const {
    formData,
    errors,
    isLoading,
    updateField,
    updatePaymentAccount,
    resetForm,
    createOrganization,
  } = useOrganizationForm({ onSuccess, onClose });

  // Reset form when panel closes
  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible, resetForm]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <DesktopSidePanel
      visible={visible}
      onClose={handleClose}
      width={520}
    >
      <DesktopPanelHeader
        title="New Organization"
        onClose={handleClose}
      />

      <DesktopPanelBody>
        <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
          {/* About Organization Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>About Organization</Text>

            <GlassFormInput
              label="Organization Name"
              value={formData.name}
              onChangeText={(text) => updateField('name', text)}
              placeholder="Organization name"
              error={errors.name}
              required
            />

            <GlassFormInput
              label="Organization Domain"
              value={formData.domain}
              onChangeText={(text) => updateField('domain', text.toLowerCase())}
              placeholder="organization-domain"
              autoCapitalize="none"
              error={errors.domain}
              required
            />
          </View>

          {/* Payment Accounts Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payment Accounts</Text>

            {formData.paymentAccounts.map((account, index) => (
              <View key={account.name} style={styles.paymentAccountField}>
                <GlassFormInput
                  label={account.description}
                  value={account.accountNumber?.toString() || ''}
                  onChangeText={(text) => updatePaymentAccount(index, text)}
                  placeholder=""
                  keyboardType="numeric"
                />
              </View>
            ))}
          </View>

          {/* Actions */}
          <View style={styles.actionsContainer}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={handleClose}
              disabled={isLoading}
              activeOpacity={0.7}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.button,
                styles.createButton,
                (isLoading) && styles.disabledButton,
              ]}
              onPress={createOrganization}
              disabled={isLoading}
              activeOpacity={0.7}
            >
              {(isLoading) ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={styles.createButtonText}>Create Organization</Text>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </DesktopPanelBody>
    </DesktopSidePanel>
  );
};
